import type { MetaDescriptor } from 'react-router';
import { data, Outlet, useNavigation } from 'react-router';
import { useTrackCurrentRoute } from '~/features/cart/state-machine/currentRouteTracker';
import { DefaultErrorBoundary } from '~/shell/components/DefaultErrorBoundary';
import { CartProvider } from '~/features/cart/CartProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { NavigationView } from '~/shell/components/MobileHeader';
import type { SelectableSearchCategory } from '~/features/catalog/components/navigation/CategoryNavigation';
import CategoryNavigation from '~/features/catalog/components/navigation/CategoryNavigation';
import { isNavigationLeaf } from '~/features/catalog/utils/categoryNavigationUtil';
import { cn } from '~/common/utils/styleUtil';
import { ScrollArea } from '~/common/components/ui/scroll-area';
import { AreaChangeConfirmationDialog } from '~/shell/components/AreaChangeConfirmationDialog';
import { WebshopMessageDialog } from '~/features/webshopmessages/components/WebshopMessageDialog';
import { getWebshopMessages } from '~/features/webshopmessages/webshopMessages.server';
import { ExtendedMobileNav } from '~/shell/components/ExtendedMobileNav';
import { Footer } from '~/shell/components/footer/Footer';
import { ScrollToTop } from '~/common/components/ScrollToTop';
import type { Route } from './+types/AreaLayout';
import { ContentLayout } from '~/shell/components/ContentLayout';
import { TgsDevTools } from '~/shell/components/TgsDevTools';
import { useHydrated } from 'remix-utils/use-hydrated';
import { RouteSubNavigationItems } from '~/shell/components/RouteSubNavigationItems';
import { getToast } from 'remix-toast';
import { toast as notify } from 'sonner';
import { PriceProvider } from '~/shell/context/priceContext';
import { SpecialDeliveryDateProvider } from '~/shell/context/specialDeliveryDateContext';
import { ClientOnly } from 'remix-utils/client-only';
import { ClientSideRouteTracker } from '~/tracking/ClientSideRouteTracker';
import { LoadingIndicator } from '~/common/components/LoadingIndicator';
import { useSpinDelay } from 'spin-delay';
import { generateHreflangMeta, generateMeta } from '~/common/seo/metadata';
import { mergeRouteModuleMeta } from '~/shell/utils/metaUtil';
import { TgArea } from '~/common/types/area';
import { Header } from '~/shell/components/Header.tsx';
import { Media } from '~/media.ts';
import { optionalLoader } from '~/shell/utils/authUtil.server';
import { urlAreaCheckMiddleware } from '~/shell/middleware/urlAreaCheckMiddleware.ts';
import { SERVER_CONFIG } from '~/config/config.server.ts';
import type { ISettingDTOData, IWebshopLoginInfoDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated.ts';
import { getIsOciSessionOnContext, getWebshopSettingsFromContext } from '~/shell/globalContext.ts';
import { useRootLoaderData } from '~/common/utils/useRootLoaderData';
import { CartDialog } from '../../features/cart/cart-dialog/CartDialog';

export const AREA_LAYOUT_ACTION_INTENT = {
  ChangeLanguage: 'changeLanguage'
};

export const middleware = [urlAreaCheckMiddleware];

const OG_LOCALE_BY_LANG: Record<string, string> = { de: 'de_CH', fr: 'fr_CH', it: 'it_CH' };

export type AreaLayoutLoaderData = Readonly<Route.ComponentProps>['loaderData'];

export const loader = optionalLoader(async ({ loginInfo, area, lang, request, url, t, api }) => {
  const { toast, headers } = await getToast(request);

  const customMetas: MetaDescriptor[] = [
    {
      property: 'og:locale',
      content: OG_LOCALE_BY_LANG[lang] ?? 'it_CH'
    },
    { name: 'robots', content: 'noindex, nofollow' }
  ];

  const canonicalUrl = `${SERVER_CONFIG.FRONTEND_URL}/${lang}/${area}`;
  const hreflangMeta = generateHreflangMeta(SERVER_CONFIG.FRONTEND_URL, area);

  const meta = generateMeta(
    {
      type: 'website',
      title:
        area === TgArea
          ? t('Transgourmet Webshop | Ihr online Lebensmittel Grosshandel')
          : t('Prodega Easy | Die Produktewelt online erleben'),
      description:
        area === TgArea
          ? t(
              'Der Transgourmet Webshop für Profis aus Gastronomie und Hotellerie: Lebensmittel ✓ Getränke ✓ Non-Food ✓ Online bestellen, zuverlässig liefern lassen!'
            )
          : t(
              'Stöbern Sie im grössten online Produkte Sortiment der Schweiz. Passende Produkte gefunden? Besuchen Sie uns im Markt!'
            ),
      url: url.href,
      siteName: 'Transgourmet | Prodega',
      image: `${url.protocol}//${url.host}/${area === TgArea ? 'assets/img/transgourmet-logo.png' : 'assets/img/prodega-logo.png'}`,
      canonical: canonicalUrl
    },
    [...customMetas, ...hreflangMeta]
  );

  let webshopLoginInfoData: IWebshopLoginInfoDtoData | undefined = undefined;

  if (loginInfo) {
    webshopLoginInfoData = {
      ...loginInfo,
      settings: getWebshopSettingsFromContext() as ISettingDTOData[]
    };
  }

  const isOciSession = getIsOciSessionOnContext();

  return data(
    {
      loginInfo: webshopLoginInfoData,
      isOciSession,
      lang,
      area,
      actionExclude: area === TgArea ? loginInfo?.actionExclude : false,
      webshopMessages: await getWebshopMessages(api, area, !!loginInfo),
      toast,
      meta
    },
    { headers }
  );
});

export const meta: Route.MetaFunction = mergeRouteModuleMeta(({ loaderData }) => {
  return loaderData?.meta ?? [];
});

export default function AreaLayout({ loaderData }: Readonly<Route.ComponentProps>) {
  const { loginInfo, area, lang, actionExclude, toast } = loaderData;
  const mainRef = useRef<HTMLElement>(null);
  const [navigationView, setNavigationView] = useState<NavigationView | null>(null);
  const { catalogItems } = useRootLoaderData();
  const isHydrated = useHydrated();
  const navigation = useNavigation();
  const [outletScrollPosition, setOutletScrollPosition] = useState<number>(0);
  useTrackCurrentRoute();
  const isOciSession = loaderData.isOciSession;
  const effectiveNavigationView = navigation.state === 'loading' && navigation.location ? null : navigationView;

  const handleMenuClick = () => {
    if (effectiveNavigationView === 'menu') {
      setNavigationView(null);
      scrollTo({ top: outletScrollPosition ?? 0, behavior: 'instant' });
    } else {
      setOutletScrollPosition(scrollY);
      scrollTo({ top: 0, behavior: 'instant' });
      setNavigationView('menu');
    }
  };

  const handleCategorySelected = (category: SelectableSearchCategory) => {
    if (isNavigationLeaf(category)) {
      setNavigationView(null);
    } else {
      scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  const handleShowAllInCategorySelected = () => {
    setNavigationView(null);
  };

  const handleActionSelected = () => {
    setNavigationView(null);
  };

  const handleNoveltiesSelected = () => {
    setNavigationView(null);
  };

  const handleSearchCategorySelected = () => {
    setNavigationView(null);
  };

  // Show loading indicator for article detail navigation.
  // Because the article detail page is rendered on the server side, we need to show a loading indicator until the page is fully loaded.
  // Otherwise, the search engines will not see the content of the article detail page.
  const isArticleDetailNavigation = useMemo(
    () => navigation.state === 'loading' && navigation.location?.pathname?.includes('/catalog/article'),
    [navigation]
  );

  const showArticleDetailNavigationProgress = useSpinDelay(isArticleDetailNavigation, { delay: 300, minDuration: 500 });

  useEffect(() => {
    if (toast?.type === 'error') {
      notify.error(toast.message);
    } else if (toast?.type === 'success') {
      notify.success(toast.message);
    } else if (toast?.type === 'info') {
      notify.info(toast.message);
    } else if (toast?.type === 'warning') {
      notify.warning(toast.message);
    }
  }, [toast]);

  return (
    <>
      <ClientOnly>{() => <ClientSideRouteTracker />}</ClientOnly>
      <div id="top" />
      {isHydrated && <TgsDevTools />}
      <WebshopMessageDialog />
      <PriceProvider flushTimeout={100} flushQueueSize={50} maxCacheItems={5000}>
        <SpecialDeliveryDateProvider flushTimeout={100} flushQueueSize={50} maxCacheItems={5000}>
          <CartProvider loginInfo={loginInfo} area={area}>
            <div className="flex flex-col min-h-screen">
              {loginInfo && <AreaChangeConfirmationDialog loginInfo={loginInfo} />}
              <ClientOnly>{() => <CartDialog />}</ClientOnly>
              <Header
                activeNavigation={effectiveNavigationView}
                loginInfo={loginInfo}
                area={area}
                onMenuClick={handleMenuClick}
                onLogoClick={() => setNavigationView(null)}
                isOciSession={isOciSession}
              />
              <main
                ref={mainRef}
                className={cn(
                  '2xl:container 2xl:mx-auto flex-1 flex shrink flex-row gap-8 relative max-w-screen w-full overflow-hidden'
                )}
              >
                <Media lessThan="desktop" className={'w-full'}>
                  {effectiveNavigationView === 'menu' ? (
                    <div key={`catalog-mobile-navigation-${lang}`} className="bg-white w-full h-full">
                      <ScrollArea>
                        <div className={'p-4'}>
                          <CategoryNavigation
                            compact={true}
                            catalogItems={catalogItems}
                            onCategorySelected={handleCategorySelected}
                            onShowAllInCategorySelected={handleShowAllInCategorySelected}
                            onActionSelected={handleActionSelected}
                            onNoveltiesSelected={handleNoveltiesSelected}
                            onSearchCategorySelected={handleSearchCategorySelected}
                            actionExclude={actionExclude}
                          />
                          <RouteSubNavigationItems />
                        </div>
                        <ExtendedMobileNav onClick={handleMenuClick} />
                      </ScrollArea>
                    </div>
                  ) : (
                    <div className={cn('flex-1')}>
                      {showArticleDetailNavigationProgress ? <LoadingIndicator /> : <Outlet />}
                    </div>
                  )}
                </Media>
                <Media greaterThanOrEqual="desktop" className={'flex-1 flex gap-8 w-full'}>
                  <ContentLayout
                    leftSidebar={
                      <>
                        <CategoryNavigation
                          key={`catalog-navigation-${lang}`}
                          catalogItems={catalogItems}
                          actionExclude={actionExclude}
                        />
                        <RouteSubNavigationItems />
                      </>
                    }
                  >
                    {showArticleDetailNavigationProgress ? <LoadingIndicator /> : <Outlet />}
                  </ContentLayout>
                </Media>
                <ScrollToTop />
              </main>
              <footer
                className={cn(
                  'bg-muted border-neutral-100 border hidden pb-8 mt-8',
                  effectiveNavigationView === null && 'block'
                )}
              >
                <Footer area={area} />
              </footer>
            </div>
          </CartProvider>
        </SpecialDeliveryDateProvider>
      </PriceProvider>
    </>
  );
}

export function ErrorBoundary() {
  return <DefaultErrorBoundary />;
}
